import {useContext, useEffect} from "react";
import ContentBody from "../common/content-body/ContentBody";
import {useTranslation} from "react-i18next";
import {useRecoilState} from "recoil";
import { repairShopsState } from "./state";
import {useMemo, useState} from "react";
import SectionHeader from "../../../util/component/SectionHeader";
import {IServerSideDatasource} from "ag-grid-community";
import { getRepairShopsObj, search, exportRS } from "./rsSearch.actions";
import {SEARCH_FILTERS_FORM_INITIAL_VALUES} from "./config";
import {useForm} from "react-hook-form";
import {INameRepairShop, IRsSearchFilters} from "./models";
import {filterObject} from "../utils";

import RsFields from "./partials/RsFields";
import RsTable from "./partials/RsTable";
import CustomLoader from "../../../util/component/CustomLoader";
import { ISortBy } from "../../booking/api/types";
import { useMutation } from "@tanstack/react-query";
import { deepClone } from "../../../util/state/commonFunctions";
import { raFilterParams } from "../ra-search/state";
import * as Mui from "@mui/material";
import FileDownloadIcon from "@mui/icons-material/FileDownload";
import FileSaver from "file-saver";
import { GlobalContext } from "../../../util/state/GlobalContextProvider";
import DownloadModal from "../rs-export/DownloadModal";
import useToggle from "../../operation/hooks/useToggle";

const SearchRs = () => {
    const {t} = useTranslation(['network', 'translation']);
    const [repairShop, setRepairShops] = useRecoilState<INameRepairShop[]>(repairShopsState);
    const [filterExpanded, setFilterExpanded] = useState(false);
    const [filterParams, setFilterParams] = useRecoilState(raFilterParams);
    const raSearchListMutation = useMutation(search);
    const raExportListMutation = useMutation(exportRS);
    const [loading, setLoading] = useState(false);
    const [openDownloadModal, setOpenDownloadModal] = useToggle();


    let filterParamsClone = deepClone(filterParams);

    const searchRsForm = useForm<IRsSearchFilters>({
        defaultValues: SEARCH_FILTERS_FORM_INITIAL_VALUES
    });

    const handleSearch = (data: IRsSearchFilters) => {
        setFilterExpanded(false);
        setFilterParams(filterObject(data, x => JSON.stringify(data[x]) !== JSON.stringify(SEARCH_FILTERS_FORM_INITIAL_VALUES[x])));
    };

    useEffect(() => {
        getRepairShopsObj()
            .then((res: any) => { setRepairShops(res?.nameCode || []) })

        if (globalData.user.legalEntities.length === 1) {
            searchRsForm.setValue('tenants', globalData.user.legalEntities);
        } else if (globalData.user.legalEntities.length > 1) {
            searchRsForm.setValue('tenants', globalData.user.legalEntities.filter((elem: any) => elem.name !== 'all'));
            searchRsForm.setValue('dealers', globalData.user.dealers.filter((elem: any) => elem.name !== 'all'));
        }

        const data = searchRsForm.getValues();
        setFilterParams(filterObject(data, x => JSON.stringify(data[x]) !== JSON.stringify(SEARCH_FILTERS_FORM_INITIAL_VALUES[x])));
    }, [])

    const {globalData} = useContext(GlobalContext);

    const serverSideDatasource: IServerSideDatasource = useMemo(() => ({
        getRows: async(params) => {
            const sortBy: ISortBy | undefined = params.request.sortModel[0] ? {
                field: params.request.sortModel[0].colId,
                order: params.request.sortModel[0].sort
            } : undefined;

            const pagination = Number(params.request.endRow) - Number(params.request.startRow);
            const { startRow: startIndex, endRow: endIndex } = params.request;

            const updatedFilterParams = { ...filterParams };

            updatedFilterParams['paramRequest'] = { pagination, sortBy, startIndex, endIndex };

            if (!!updatedFilterParams.tenants) {
                const ind = updatedFilterParams.tenants.findIndex((elem: any) => elem.name === 'all');
                if (ind > -1) {
                    updatedFilterParams.tenants =
                        globalData.user.legalEntities
                            .filter((elem: any) => elem.name !== 'all')
                            .map((elem: any) => elem.name);
                } else {
                    updatedFilterParams.tenants = updatedFilterParams.tenants.map((elem: any) => elem.name);
                }
            }

            raSearchListMutation.mutateAsync(updatedFilterParams)
                .then((response: any) => {
                    params.success({
                        rowData: response.repairShop,
                        rowCount: response.paramResponse.totCount
                    });
                    response.paramResponse.totCount === 0 && params.api.showNoRowsOverlay();
                })
                .catch(() => {
                    params.fail();
                    params.api.hideOverlay();
                });
        }
    }), [filterParams]);
	
	const getFileName = () => {

        if (searchRsForm.control._formValues){

            if (searchRsForm.control._formValues.nameCode && searchRsForm.control._formValues.nameCode.length > 0)
                return "repair_shops_filtered.xlsx";
            else if (searchRsForm.control._formValues.identificationCode && searchRsForm.control._formValues.identificationCode.length > 0)
                return "repair_shops_filtered.xlsx";
            else if (searchRsForm.control._formValues.dmsType && searchRsForm.control._formValues.dmsType.length > 0)
                return "repair_shops_filtered.xlsx";
            else if (searchRsForm.control._formValues.category && searchRsForm.control._formValues.category.length > 0)
                return "repair_shops_filtered.xlsx";
            else if (searchRsForm.control._formValues.currentStatus && searchRsForm.control._formValues.currentStatus.length > 0)
                return "repair_shops_filtered.xlsx";
            else if (searchRsForm.control._formValues.currentAttribute && searchRsForm.control._formValues.currentAttribute.length > 0)
                return "repair_shops_filtered.xlsx";
            else if (searchRsForm.control._formValues.region && searchRsForm.control._formValues.region.length > 0)
                return "repair_shops_filtered.xlsx";            
            else if (searchRsForm.control._formValues.province && searchRsForm.control._formValues.province.length > 0)
                return "repair_shops_filtered.xlsx";
            else if (searchRsForm.control._formValues.city && searchRsForm.control._formValues.city.length > 0)
                return "repair_shops_filtered.xlsx";
            else if (searchRsForm.control._formValues.chargingStationStatus && searchRsForm.control._formValues.chargingStationStatus.length > 0)
                return "repair_shops_filtered.xlsx";
            else if (searchRsForm.control._formValues.chargingStationIdentificationCode && searchRsForm.control._formValues.chargingStationIdentificationCode.length > 0)
                return "repair_shops_filtered.xlsx";
            else if (searchRsForm.control._formValues.certificationProtocolIdentifyingCode && searchRsForm.control._formValues.certificationProtocolIdentifyingCode.length > 0)
                  return "repair_shops_filtered.xlsx";
            else
                return "repair_shops.xlsx";
        } else
            return "repair_shops.xlsx";

    }

    const handleDownloadExcel = () => {
        setLoading(true);
    
        if (!filterParamsClone) {
            filterParamsClone = {};
        }

        if (!!filterParamsClone.tenants) {
            const ind = filterParamsClone.tenants.findIndex((elem: any) => elem.name === 'all');
            if (ind > -1) {
                filterParamsClone.tenants =
                    globalData.user.legalEntities
                        .filter((elem: any) => elem.name !== 'all')
                        .map((elem: any) => elem.name);
            } else {
                filterParamsClone.tenants = filterParamsClone.tenants.map((elem: any) => elem.name);
            }
        }

        // if (!!filterParamsClone.dealers) {
        //     const ind = filterParamsClone.dealers.findIndex((elem: any) => elem.name === 'all');
        //     if (ind > -1) {
        //         filterParamsClone.dealers =
        //             globalData.user.legalEntities
        //                 .filter((elem: any) => elem.name !== 'all')
        //                 .map((elem: any) => elem.name);
        //     } else {
        //         filterParamsClone.dealers = filterParamsClone.dealers.map((elem: any) => elem.name);
        //     }
        // }

        raExportListMutation.mutateAsync(filterParamsClone)
            .then((response: any) => {
                setLoading(false);
                if (response.status === 200) {
                    console.log(JSON.stringify(response));
                    const filename = getFileName();            
                    var blob = new Blob([response.data], {
                        type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
                    });
                    FileSaver.saveAs(blob, filename);
                } else setOpenDownloadModal();
            });

            /*
        raExportListMutation.mutateAsync(filterParamsClone)
            .then((response: any) => {
              setLoading(false);
    
              console.log(JSON.stringify(response));
              const filename = getFileName();            
              var blob = new Blob([response.data], {
                type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
              });
              FileSaver.saveAs(blob, filename);
            });
            */
        
      };


    return (
        <>
            <SectionHeader title={t('searchRs.title')}/>
            <ContentBody>
                <RsFields
                    handleSearch={handleSearch}
                    repairShop={repairShop}
                    searchRsForm={searchRsForm}
                    filterExpandedState={[filterExpanded, setFilterExpanded]}
                />
				<Mui.Box
                        sx={{
                            backgroundColor: "white",
                            border: " 1px solid #dbe4e9",
                            borderRadius: "2px",
                            padding: "9px 1px 3px 2px",
                        }}
                        >

                        <Mui.Grid
                            container
                            spacing={2}
                            alignItems="center"
                            direction="row"
                            justifyContent="flex-end"
                        >
                        <Mui.Grid item>
                            <Mui.Button
                                variant="text"
                                size="icon"
                                onClick={handleDownloadExcel}
                                color="accent"
                                //disabled={disableDownloadExcel}
                            >
                                <Mui.Tooltip title={t("searchDms.download")}>
                                <FileDownloadIcon />
                                </Mui.Tooltip>
                            </Mui.Button>
                            </Mui.Grid>

                        </Mui.Grid>
                </Mui.Box>
                <RsTable serverSideDatasource={serverSideDatasource} />
            </ContentBody>
            <CustomLoader loading={raSearchListMutation.isLoading || loading} />

            {openDownloadModal && (
                <DownloadModal setOpenDownloadModal={setOpenDownloadModal} />
            )}
        </>
    )
}

export default SearchRs;
